<template>
    <div class="page-wrapper" v-if="$route.name!=='Set Password' && $route.name!=='Reset Password' && $route.name!=='App Store'">
        <div class="">
            <Header v-if="showShellLayout" />
        </div>
        <div class="page-body-wrapper">
            <Sidebar v-if="showShellLayout" class="page-sidebar" />
            <div class="page-body" :class="{ 'page-body--fullbleed': !showShellLayout }">
                <!-- Key by route only; terminology updates via terminologyRevision in i18n/filters (not full remount). -->
                <router-view :key="$route.fullPath" />
            </div>
            <Footer v-if="showShellLayout" />
            <!-- END PAGE CONTENT -->
        </div>
        <!-- END MAIN CONTENT -->
    </div>
    <div class="page-wrapper" v-if="$route.name=='Set Password' || $route.name=='Reset Password' || $route.name=='App Store'">
        <div class="page-body">
            <router-view />
        </div>
    </div>
    <AppChatbot v-if="showChatbot && chatbotReady"
                :spliytgini-consent="spliytginiConsent"
                :chat-open-token="chatOpenToken"
                @show-consent="openGenieConsent" />
    <div v-if="showGeniePrivacyModal"
         class="genie-consent-overlay"
         @click.self="closeGeniePrivacy">
        <div class="genie-consent-modal">
            <!-- CLOSE -->
            <button type="button"
                    class="genie-close-btn"
                    aria-label="Close"
                    :disabled="savingConsent"
                    @click="closeGeniePrivacy">
                ×
            </button>
            <!-- LOCK ICON -->
            <!--<div class="genie-lock-box">
                            <svg width="24"
                                 height="24"
                                 viewBox="0 0 24 24"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg">
                                <rect x="5"
                                      y="10"
                                      width="14"
                                      height="11"
                                      rx="2"
                                      stroke="#B98735"
                                      stroke-width="2" />
                                <path d="M8 10V7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7V10"
                                      stroke="#B98735"
                                      stroke-width="2"
                                      stroke-linecap="round" />
                                <circle cx="12"
                                        cy="15.5"
                                        r="1.3"
                                        fill="#B98735" />
                            </svg>
                        </div>
            -->
            <!-- TITLE -->
            <h2 class="genie-consent-title">
                SpliytGenie — Data Sharing Notice
            </h2>
            <!-- CONTENT -->
            <div class="genie-consent-content">
                <p>
                    SpliytGenie helps you complete
                    <strong>Onboarding</strong> today, and later
                    <strong>
                        Tickets, Expenses, Chores, Documents, Service
                        Providers, Events, Friends, Roommates
                    </strong>
                    and other modules in the app. Here's how it works:
                </p>
                <ul>
                    <li>
                        Our own system retrieves the relevant data for your request —
                        for example your onboarding answers, a ticket, an expense,
                        an event, or a document record — from our database.
                    </li>
                    <li>
                        Your message and that data are sent to
                        <strong>Groq</strong>, a third-party AI service,
                        solely to generate a response.
                    </li>
                    <li>
                        Groq returns the generated response, which is shown to you
                        in the chat.
                    </li>
                </ul>
                <div class="genie-groq-notice">
                    Groq is used only to generate response text.
                    This data is not stored at any point in this process,
                    is not sold, and is not used by Groq to train its AI models.
                </div>
                <p class="genie-decline-text">
                    You can decline and still use the rest of the app as normal —
                    only the AI Assistant chat feature will be unavailable.
                </p>
                <a href="https://roomdera.com/privacypolicy#ai-policy"
                   target="_blank"
                   rel="noopener noreferrer"
                   class="genie-privacy-link"
                   @click.stop>
                    Learn more in our Privacy Policy
                    <span class="privacy-arrow">→</span>
                </a>
                <div class="genie-consent-actions">
                    <button type="button"
                            class="genie-allow-btn"
                            :disabled="savingConsent"
                            @click="allowGenie">
                        <span v-if="!savingConsent">
                            Allow
                        </span>
                        <span v-else>
                            Saving...
                        </span>
                    </button>
                    <button type="button"
                            class="genie-deny-btn"
                            :disabled="savingConsent"
                            @click="denyGenie">
                        Don't Allow
                    </button>
                </div>
            </div>
            <!-- BUTTONS -->
        </div>
    </div>
</template>
<script>
    import UserService from '@/service/user.service';
    import { defineAsyncComponent } from 'vue';
    import Header from "@/layouts/header/Header.vue"
    import Sidebar from '@/layouts/sidebar/Sidebar.vue';
    import Footer from "@/layouts/footer/footer.vue";
    import { initShellLayoutWhenReady } from '@/helpers/shellLayout';
    import { isSessionValid, isLoginPath } from '@/helpers/authSession';
    const AppChatbot = defineAsyncComponent(() => import("@/layouts/ChatBot/chatbot.vue"));
    //import minJs from "@/assets/CustomJs/jquery-3.4.1.min.js"
    //import boostrp from "@/assets/CustomJs/bootstrap.min.js"
    //import customjs from "@/assets/CustomJs/front.js"
    export default {
        name: 'LayoutName',
        components: {
            Header,
            Sidebar, Footer, AppChatbot
        },
        created() {
        },
        async mounted() {
            await this.getSpliytginiConsent();
            this.scheduleShellLayoutSync();
            const enableChatbot = () => {
                this.chatbotReady = true;
            };
            if (
                typeof window !== 'undefined' &&
                typeof window.requestIdleCallback === 'function'
            ) {
                window.requestIdleCallback(
                    enableChatbot,
                    { timeout: 400 }
                );
            } else {
                setTimeout(enableChatbot, 0);
            }
        },
        watch: {
            showShellLayout(visible) {
                if (visible) {
                    this.scheduleShellLayoutSync();
                }
            }
        },
        methods: {
            normalizeConsentValue(data) {
                if (data === true || data === 1 || data === '1' || data === 'true') return true;
                if (data === false || data === 0 || data === '0' || data === 'false') return false;
                return null;
            },
            /**
             * Click chat icon → always hit GetSpliytginiConsent (database).
             * true  → do NOT show popup, open chat
             * false → show popup
             */
            async openGenieConsent() {
                await this.getSpliytginiConsent();
                if (this.spliytginiConsent === true) {
                    this.showGeniePrivacyModal = false;
                    // Tell chatbot to open (works even if consent was already true)
                    this.chatOpenToken += 1;
                    return;
                }
                // false or null → show consent popup
                this.showGeniePrivacyModal = true;
            },
            closeGeniePrivacy() {
                if (this.savingConsent) {
                    return;
                }
                this.showGeniePrivacyModal = false;
            },
            async allowGenie() {
                if (this.savingConsent) {
                    return;
                }
                this.savingConsent = true;
                try {
                    const model = {
                        CompanyId: localStorage.getItem('companyId'),
                        SpliytginiConsent: true
                    };
                    await UserService.UpdateSpliytginiConsent(model);
                    this.spliytginiConsent = true;
                    this.showGeniePrivacyModal = false;
                    // Open chat after Allow
                    this.chatOpenToken += 1;
                } catch (error) {
                    console.error(error);
                } finally {
                    this.savingConsent = false;
                }
            },
            async denyGenie() {
                if (this.savingConsent) {
                    return;
                }
                this.savingConsent = true;
                try {
                    const model = {
                        CompanyId: localStorage.getItem('companyId'),
                        SpliytginiConsent: false
                    };
                    await UserService.UpdateSpliytginiConsent(model);
                    this.spliytginiConsent = false;
                    this.showGeniePrivacyModal = false;
                } catch (error) {
                    console.error(error);
                } finally {
                    this.savingConsent = false;
                }
            },
            /**
             * Load consent from database (source of truth).
             * Called on portal load and every time user clicks the chat icon.
             */
            async getSpliytginiConsent() {
                const companyId = localStorage.getItem('companyId');
                if (!companyId) {
                    this.spliytginiConsent = null;
                    return;
                }
                try {
                    const response =
                        await UserService.GetSpliytginiConsent(companyId);
                    let data =
                        response?.Data ??
                        response?.data ??
                        response;
                    if (data && typeof data === 'object') {
                        data =
                            data.SpliytginiConsent ??
                            data.spliytginiConsent ??
                            data.Consent ??
                            data.consent ??
                            data.result ??
                            data.Result;
                    }
                    const normalized = this.normalizeConsentValue(data);
                    if (normalized === true) {
                        this.spliytginiConsent = true;
                    } else if (normalized === false) {
                        this.spliytginiConsent = false;
                    } else {
                        this.spliytginiConsent = null;
                    }
                } catch (error) {
                    this.spliytginiConsent = null;
                }
            },
            scheduleShellLayoutSync() {
                this.$nextTick(() => {
                    initShellLayoutWhenReady();
                });
            }
        },
        data() {
            return {
                chatbotReady: false,
                spliytginiConsent: null,
                showGeniePrivacyModal: false,
                savingConsent: false,
                chatOpenToken: 0
            };
        },
        computed: {
            isChecklistPopupMode() {
                return this.$route?.path === '/onboardinglist' && this.$route?.query?.checklistPopup === 'true';
            },
            isAuthOnlyPage() {
                return this.$route.name === 'Set Password'
                    || this.$route.name === 'Reset Password'
                    || this.$route.name === 'App Store';
            },
            showShellLayout() {
                // Re-evaluate when route changes so shell appears after client-side post-login navigation.
                void this.$route?.fullPath;
                const path = this.$route?.path || '';
                const routeName = this.$route?.name;
                // Login route name is "Login" (not "login"); also honor path + meta from the route table.
                if (isLoginPath(path) ||
                    routeName === 'Login' ||
                    routeName === 'login' ||
                    routeName === 'account/resetpassword' ||
                    this.$route.meta?.hideLayout ||
                    this.$route.meta?.showHeader === false ||
                    this.isChecklistPopupMode) {
                    return false;
                }
                // Require a valid session (not merely a leftover expired token).
                return isSessionValid();
            },
            showChatbot() {
                const userType = localStorage.getItem("userType");
                return !this.$route.meta?.hideLayout
                    && !this.isChecklistPopupMode
                    && !this.isAuthOnlyPage
                    && !['usertype11', 'usertype12', 'usertype22'].includes(userType);
            }
        }
    }
</script>

<style>
    /* When Header/Sidebar are hidden (meta.hideLayout), remove the reserved sidebar/header space. */
    .page-body-wrapper .page-body.page-body--fullbleed {
        margin-left: 0 !important;
        margin-top: 0 !important;
        padding: 0 !important;
        min-height: 100vh !important;
        background: transparent !important;
    }

    /* =========================================================
       SPLIYTGENIE CONSENT MODAL
       (copied from OnboardingChoice.vue — that component's
       <style> only loads when OnboardingChoice.vue itself is
       mounted, so this modal had no styling on pages that only
       render layout.vue's copy of it)
    ========================================================= */

    /* =========================================================
       CONSENT OVERLAY
    ========================================================= */

    .genie-consent-overlay {
        position: fixed;
        inset: 0;
        z-index: 999999;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
        background: rgba(20, 28, 40, 0.64);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
    }


    /* =========================================================
       CONSENT MODAL
    ========================================================= */

    .genie-consent-modal {
        position: relative;
        width: 95%;
        max-width: 800px;
        background: #ffffff;
        border-radius: 22px;
        padding: 30px 30px 26px;
        box-shadow: 0 30px 80px rgba(0, 0, 0, 0.30);
        animation: genieConsentOpen 0.22s ease-out;
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        color: #27344b;
    }


    .genie-close-btn {
        position: absolute;
        top: 18px;
        right: 18px;
        width: 32px;
        height: 32px;
        border: none;
        background: #f2f3f5;
        border-radius: 50%;
        font-size: 20px;
        line-height: 1;
        color: #6b7280;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        transition: background 0.15s ease, color 0.15s ease;
    }

        .genie-close-btn:hover {
            background: #e7e9ed;
            color: #1f2937;
        }

        .genie-close-btn:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }


    .genie-lock-box {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #fbf1de;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 18px;
    }


    .genie-consent-title {
        margin: 0 0 14px;
        font-size: 22px;
        line-height: 1.3;
        font-weight: 800;
        color: #14263d;
        padding-right: 34px;
    }


    .genie-consent-content {
        font-size: 13.5px;
        line-height: 1.6;
        color: #465269;
    }

        .genie-consent-content p {
            margin: 0 0 14px;
        }

        .genie-consent-content strong {
            color: #1e2d45;
        }

        .genie-consent-content ul {
            list-style: none;
            margin: 12px 0 17px;
            padding: 0;
        }

        .genie-consent-content li {
            position: relative;
            padding-left: 17px;
            margin-bottom: 12px;
        }

            .genie-consent-content li:last-child {
                margin-bottom: 0;
            }

            .genie-consent-content li::before {
                content: '';
                position: absolute;
                left: 0;
                top: 8px;
                width: 8px;
                height: 8px;
                border-radius: 50%;
                background: #bd8a39;
                box-shadow: 0 0 0 3px rgba(189, 138, 57, 0.13);
            }


    /* =========================================================
       GROQ BOX
    ========================================================= */

    .genie-groq-notice {
        margin: 17px 0 16px;
        padding: 12px 13px;
        border: 1px solid #ead8b8;
        background: #fbf9f4;
        border-radius: 10px;
        color: #718096;
        font-size: 13px;
        line-height: 1.55;
    }


    /* =========================================================
       DECLINE TEXT
    ========================================================= */

    .genie-decline-text {
        margin-top: 0 !important;
        margin-bottom: 17px !important;
    }


    /* =========================================================
       PRIVACY POLICY
    ========================================================= */

    .genie-privacy-link {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: #b57925;
        font-size: 13px;
        font-weight: 700;
        text-decoration: none;
        cursor: pointer;
    }

        .genie-privacy-link:hover {
            color: #95641e;
            text-decoration: underline;
        }


    .privacy-arrow {
        font-size: 15px;
        line-height: 1;
    }


    /* =========================================================
       ACTION BUTTONS
    ========================================================= */

    .genie-consent-actions {
        margin-top: 21px;
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

        .genie-consent-actions button {
            width: 100%;
            height: 47px;
            border-radius: 11px;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
        }

            .genie-consent-actions button:disabled {
                opacity: 0.65;
                cursor: not-allowed;
            }


    /* =========================================================
       ALLOW BUTTON
    ========================================================= */

    .genie-allow-btn {
        border: 1px solid #14263d;
        background: #14263d;
        color: #ffffff;
        box-shadow: 0 8px 16px rgba(20, 38, 61, 0.18);
    }

        .genie-allow-btn:hover:not(:disabled) {
            background: #1b314d;
            border-color: #1b314d;
            box-shadow: 0 10px 20px rgba(20, 38, 61, 0.23);
        }


    /* =========================================================
       DON'T ALLOW
    ========================================================= */

    .genie-deny-btn {
        border: 1px solid #d9dce1;
        background: #ffffff;
        color: #465269;
    }

        .genie-deny-btn:hover:not(:disabled) {
            background: #fafafa;
            border-color: #c8ccd3;
        }


    /* =========================================================
       MOBILE CONSENT
    ========================================================= */

    @media (max-width: 767px) {
        .genie-lock-box {
            display: none !important;
        }

        .genie-consent-title {
            position: absolute;
            left: 0px;
            top: 0px;
            padding: 10px;
            background: #14263d;
            color: #fff;
            width: 100%;
            z-index: 9;
            padding-right: 25px;
        }

        .genie-consent-modal {
            overflow: hidden !important;
        }

        .genie-consent-content {
            width: 100%;
            overflow: auto;
            padding: 99px 10px 40px 10px;
            height: 100vh;
        }

        .genie-consent-modal {
            padding: 0px !important;
        }

        .genie-close-btn {
            z-index: 10;
        }
    }

    @media (max-width: 576px) {

        .genie-consent-overlay {
            padding: 12px;
        }

        .genie-consent-modal {
            max-width: 100%;
            border-radius: 20px;
            padding: 25px 22px 21px;
            max-height: calc(100vh - 24px);
        }

        .genie-close-btn {
            top: 14px;
            right: 14px;
        }

        .genie-lock-box {
            width: 50px;
            height: 50px;
            margin-bottom: 17px;
        }

        .genie-consent-title {
            font-size: 21px;
            line-height: 1.25;
        }

        .genie-consent-content {
            font-size: 13px;
            line-height: 1.52;
        }

        .genie-groq-notice {
            font-size: 12.8px;
            line-height: 1.5;
        }

        .genie-consent-actions button {
            height: 46px;
        }
    }
</style>
